import { Loader2, MessageSquare, RefreshCw } from 'lucide-react';
import { useCallback, useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  type ContactUrgency,
  type TreatmentContactQueueItem,
  useTreatmentContactQueueListQuery,
} from '@/services/api/generated/treatmentContactQueueService';

import { SmsComposerModal } from '../sms-composer/SmsComposerModal';
import {
  ContactQueueFilters,
  type ContactQueueFiltersState,
} from './ContactQueueFilters';
import { ContactQueueItem } from './ContactQueueItem';

const ITEMS_PER_PAGE = 25;

const UrgencyCountBadge = ({
  label,
  count,
  color,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  color: string;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
      isActive ? 'ring-2 ring-offset-1' : 'hover:bg-zinc-100',
      color,
    )}
  >
    <span
      className={cn(
        'h-2 w-2 rounded-full',
        color.includes('red')
          ? 'bg-red-500'
          : color.includes('orange')
            ? 'bg-orange-500'
            : color.includes('yellow')
              ? 'bg-yellow-500'
              : 'bg-zinc-400',
      )}
    />
    {label} ({count})
  </button>
);

export const ContactQueueTab = () => {
  const [filters, setFilters] = useState<ContactQueueFiltersState>({
    urgency: [],
    daysOverdueMin: null,
    requiresEscalation: false,
  });
  const [offset, setOffset] = useState(0);
  const [selectedItem, setSelectedItem] =
    useState<TreatmentContactQueueItem | null>(null);

  const {
    data: queueData,
    isLoading,
    isFetching,
    refetch,
  } = useTreatmentContactQueueListQuery({
    urgency: filters.urgency.length > 0 ? filters.urgency : undefined,
    daysOverdueMin: filters.daysOverdueMin ?? undefined,
    requiresEscalation: filters.requiresEscalation || undefined,
    limit: ITEMS_PER_PAGE,
    offset,
  });

  const handleSendSms = useCallback((item: TreatmentContactQueueItem) => {
    setSelectedItem(item);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const handleSmsSent = useCallback(() => {
    setSelectedItem(null);
    refetch();
  }, [refetch]);

  const handleUrgencyFilter = (urgency: ContactUrgency) => {
    if (filters.urgency.includes(urgency)) {
      setFilters({ ...filters, urgency: [] });
    } else {
      setFilters({ ...filters, urgency: [urgency] });
    }
    setOffset(0);
  };

  const totalPages = queueData
    ? Math.ceil(queueData.total_count / ITEMS_PER_PAGE)
    : 0;
  const currentPage = Math.floor(offset / ITEMS_PER_PAGE) + 1;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-zinc-500">
              Clients needing treatment check-in contact
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="gap-1.5"
            >
              <RefreshCw
                className={cn('h-4 w-4', isFetching && 'animate-spin')}
              />
              Refresh
            </Button>
            <ContactQueueFilters
              filters={filters}
              onFiltersChange={(newFilters) => {
                setFilters(newFilters);
                setOffset(0);
              }}
            />
          </div>
        </div>

        {/* Urgency count badges */}
        {queueData && (
          <div className="mt-4 flex items-center gap-3">
            <UrgencyCountBadge
              label="Escalate"
              count={queueData.escalation_count ?? 0}
              color="bg-red-50 text-red-700 ring-red-500"
              isActive={
                filters.urgency.length === 1 &&
                filters.urgency[0] === 'Escalate to Attorney'
              }
              onClick={() => handleUrgencyFilter('Escalate to Attorney')}
            />
            <UrgencyCountBadge
              label="Urgent"
              count={queueData.urgent_count ?? 0}
              color="bg-orange-50 text-orange-700 ring-orange-500"
              isActive={
                filters.urgency.length === 1 && filters.urgency[0] === 'Urgent'
              }
              onClick={() => handleUrgencyFilter('Urgent')}
            />
            <UrgencyCountBadge
              label="Overdue"
              count={queueData.overdue_count ?? 0}
              color="bg-yellow-50 text-yellow-700 ring-yellow-500"
              isActive={
                filters.urgency.length === 1 && filters.urgency[0] === 'Overdue'
              }
              onClick={() => handleUrgencyFilter('Overdue')}
            />
            <UrgencyCountBadge
              label="Routine"
              count={queueData.routine_count ?? 0}
              color="bg-zinc-50 text-zinc-700 ring-zinc-400"
              isActive={
                filters.urgency.length === 1 && filters.urgency[0] === 'Routine'
              }
              onClick={() => handleUrgencyFilter('Routine')}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
          </div>
        ) : !queueData || queueData.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <MessageSquare className="h-12 w-12 text-zinc-300 mb-3" />
            <h3 className="text-sm font-medium text-zinc-900">
              No clients need contact
            </h3>
            <p className="text-sm text-zinc-500 mt-1">
              All clients are up to date on treatment check-ins
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {queueData.items.map((item) => (
              <ContactQueueItem
                key={item.matter_id}
                item={item}
                onSendSms={handleSendSms}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {queueData && queueData.total_count > ITEMS_PER_PAGE && (
        <div className="flex-shrink-0 border-t bg-white px-6 py-3 flex items-center justify-between">
          <p className="text-sm text-zinc-500">
            Showing {offset + 1}-
            {Math.min(offset + ITEMS_PER_PAGE, queueData.total_count)} of{' '}
            {queueData.total_count} clients
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - ITEMS_PER_PAGE))}
            >
              Previous
            </Button>
            <span className="text-sm text-zinc-600">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + ITEMS_PER_PAGE >= queueData.total_count}
              onClick={() => setOffset(offset + ITEMS_PER_PAGE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* SMS Composer Modal */}
      {selectedItem && (
        <SmsComposerModal
          open={!!selectedItem}
          onOpenChange={(open) => !open && handleCloseModal()}
          matterId={selectedItem.matter_id}
          clientName={selectedItem.client_name}
          clientPhone={selectedItem.client_phone ?? undefined}
          onSmsSent={handleSmsSent}
        />
      )}
    </div>
  );
};
